/**
 * TaskSidebar Component
 * Main sidebar container displaying tasks grouped into four sections:
 * Scheduled on calendar, Today (selected day), Unassigned (To-dos), and Done.
 */

import type { Task } from '@signalvote/shared';
import { TaskStatus } from '@signalvote/shared';
import { useState } from 'react';
import { format } from 'date-fns';
import { getCampaignTodayString, isToday } from '@/lib/calendarUtils';
import { Loader2 } from 'lucide-react';
import { useCalendarStore } from '@/stores/calendarStore';
import { useTaskStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';
import { useMayManageTasks } from '@/hooks';
import { useTasks, useCompleteTask, useReopenTask, useAcceptTask, useDeclineTask, useUpdateTask } from '@/hooks/queries';
import { mapTaskListItemToTask, formatDurationShort } from '@/lib/taskAssignmentUtils';
import { TaskSidebarSection } from './TaskSidebarSection';
import { TaskQuickAdd } from './TaskQuickAdd';
import { SidebarDatePicker } from './SidebarDatePicker';
import { SidebarSettingsPopover } from './SidebarSettingsPopover';

// ============================================================================
// Constants
// ============================================================================

const SIDEBAR_WIDTH = 300;

// ============================================================================
// Helpers
// ============================================================================

/** Sum durations and return a formatted label like "1h", "15m", "2.5h" */
function sumDuration(tasks: Task[]): string | null {
  const total = tasks.reduce((sum, t) => sum + (t.durationMinutes ?? 0), 0);
  return formatDurationShort(total);
}

// ============================================================================
// Component
// ============================================================================

export interface TaskSidebarProps {
  /** Called when a task card is clicked to open details */
  onTaskClick?: (task: Task) => void;
  /** Whether task cards can be dragged to the calendar */
  draggable?: boolean;
}

export function TaskSidebar({ onTaskClick, draggable }: TaskSidebarProps) {
  // Fetch tasks relevant to current user (assigned to me + open/claimable)
  const tasksRead = useTasks({ myTasks: true, includeCreatedByMe: true, pageSize: 100 });
  const { user } = useAuth();
  const mayManageTasks = useMayManageTasks();
  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const acceptTask = useAcceptTask();
  const declineTask = useDeclineTask();
  const updateTask = useUpdateTask();

  const tasks: Task[] = tasksRead.status === 'ready' ? tasksRead.data.items.map(mapTaskListItemToTask) : [];

  // Get selected date from calendar store
  const selectedDate = useCalendarStore((state) => state.selectedDate);

  // Format selected date for querying tasks (YYYY-MM-DD)
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');

  // Determine section title for selected day
  const selectedDayTitle = isToday(selectedDate)
    ? 'Today'
    : format(selectedDate, 'EEE d');

  // Filter toggles
  const showDelegated = useCalendarStore((state) => state.showDelegated);
  const setShowDelegated = useCalendarStore((state) => state.setShowDelegated);
  const [showAllTasks, setShowAllTasks] = useState(false);

  const visibleTasks = showDelegated
    ? tasks
    : tasks.filter((t) => !(t.createdById === user?.id && t.assignedToId && t.assignedToId !== user?.id));

  // Apply category filters from calendar store
  const activeFilters = useCalendarStore((state) => state.activeFilters);

  const filteredTasks = activeFilters.includes('all')
    ? visibleTasks
    : visibleTasks.filter((t) => {
        // Entity-type filters (shifts, calendar) don't affect task visibility in sidebar
        const categoryFilters = activeFilters.filter((f) => f !== 'shifts' && f !== 'calendar');
        if (categoryFilters.length === 0) return true; // only entity-type filters active
        if (categoryFilters.includes('uncategorized') && !t.categoryId) return true;
        if (t.categoryId && categoryFilters.includes(t.categoryId)) return true;
        return false;
      });

  // ---- Section filters ----

  const today = getCampaignTodayString();

  const notDone = (t: Task) =>
    t.status !== TaskStatus.Completed && t.status !== TaskStatus.Declined;

  const isOverdue = (t: Task) =>
    !!t.dueDate && t.dueDate < today && notDone(t);

  // Overdue: past due date, not done
  const overdueTasks = filteredTasks.filter(isOverdue);

  // Scheduled on calendar: has date + time, not done, not overdue
  // In "show all" mode: all future scheduled tasks; otherwise: only selected day
  const scheduledTasks = filteredTasks.filter(
    (t) =>
      !!t.dueDate &&
      t.dueTime &&
      notDone(t) &&
      !isOverdue(t) &&
      (showAllTasks ? t.dueDate >= today : t.dueDate === selectedDateStr)
  );

  // Today / selected day / Upcoming: has date, NO time, not done, not overdue
  // In "show all" mode: all future date-only tasks; otherwise: only selected day
  const selectedDayTasks = filteredTasks.filter(
    (t) =>
      !!t.dueDate &&
      !t.dueTime &&
      notDone(t) &&
      !isOverdue(t) &&
      (showAllTasks ? t.dueDate >= today : t.dueDate === selectedDateStr)
  );

  // Unassigned (To-dos): no date at all, not done
  const unscheduledTasks = filteredTasks.filter(
    (t) => !t.dueDate && notDone(t)
  );

  // Done: completed
  const doneTasks = filteredTasks.filter(
    (t) => t.status === TaskStatus.Completed
  );

  // "Show all tasks" mode: single flat list of all non-overdue, non-done tasks
  // sorted by date (dated tasks first, then undated)
  const allActiveTasks = showAllTasks
    ? filteredTasks
        .filter((t) => notDone(t) && !isOverdue(t))
        .sort((a, b) => {
          if (a.dueDate && !b.dueDate) return -1;
          if (!a.dueDate && b.dueDate) return 1;
          if (a.dueDate && b.dueDate) {
            if (a.dueDate !== b.dueDate) return a.dueDate.localeCompare(b.dueDate);
            if (a.dueTime && b.dueTime) return a.dueTime.localeCompare(b.dueTime);
            if (a.dueTime && !b.dueTime) return -1;
            if (!a.dueTime && b.dueTime) return 1;
          }
          return 0;
        })
    : [];

  // ---- Handlers ----

  const handleCheckboxChange = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    if (task.status === TaskStatus.Completed) {
      // Uncheck: reopen back to Pending
      useTaskStore.getState().updateTaskStatus(taskId, TaskStatus.Pending);
      reopenTask.mutate(taskId);
    } else {
      // Check: mark as completed
      useTaskStore.getState().updateTaskStatus(taskId, TaskStatus.Completed);
      completeTask.mutate(taskId);
    }
  };

  const handleAccept = (taskId: string) => {
    useTaskStore.getState().updateTaskStatus(taskId, TaskStatus.Accepted);
    acceptTask.mutate(taskId);
  };

  const handleReject = (taskId: string) => {
    useTaskStore.getState().updateTaskStatus(taskId, TaskStatus.Declined);
    declineTask.mutate(taskId);
  };

  const handleAssign = (taskId: string, assigneeId: string, assigneeName: string) => {
    useTaskStore.getState().updateTask(taskId, { assignedToId: assigneeId, assignedToName: assigneeName });
    updateTask.mutate({ id: taskId, data: { assignedToId: assigneeId } });
  };

  // Shared section props
  const sectionProps = {
    currentUserId: user?.id,
    draggable: !!draggable && mayManageTasks,
    onTaskCheckboxChange: handleCheckboxChange,
    onTaskClick: onTaskClick ?? (() => {}),
    onAccept: handleAccept,
    onReject: handleReject,
    onAssign: mayManageTasks ? handleAssign : undefined,
  };

  return (
    <div
      className="h-full flex flex-col border-r border-calendar-border bg-gray-50/70 shrink-0"
      style={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        maxWidth: SIDEBAR_WIDTH,
      }}
    >
      {/* Quick Add Input */}
      <div className="p-3 border-b border-calendar-border">
        {mayManageTasks && <TaskQuickAdd />}
        <div className={mayManageTasks ? 'flex items-center justify-between mt-2' : 'flex items-center justify-between'}>
          {!showAllTasks && <SidebarDatePicker />}
          {showAllTasks && <span className="text-xs font-medium text-muted-foreground">All tasks</span>}
          <SidebarSettingsPopover
            showDelegated={showDelegated}
            onShowDelegatedChange={setShowDelegated}
            showAllTasks={showAllTasks}
            onShowAllTasksChange={setShowAllTasks}
          />
        </div>
      </div>

      {/* Scrollable Task Sections */}
      <div className="flex-1 overflow-y-auto py-3">
        {tasksRead.status === 'failed' || tasksRead.status === 'offline' ? (
          <div role="status" className="px-4 py-6 text-center text-sm text-muted-foreground">
            {tasksRead.status === 'offline' ? "You're offline, so tasks can't load." : "Couldn't load tasks. Try again shortly."}
          </div>
        ) : tasksRead.status !== 'ready' ? (
          <div role="status" aria-label="Loading tasks" className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : showAllTasks ? (
            <>
              {/* Overdue */}
              <TaskSidebarSection
                title="Overdue"
                tasks={overdueTasks}
                badgeError
                hideDuration
                titleClassName="text-destructive"
                {...sectionProps}
              />

              {/* All active tasks: flat list sorted by date */}
              <TaskSidebarSection
                title="All tasks"
                tasks={allActiveTasks}
                totalDuration={sumDuration(allActiveTasks)}
                showDateChip
                {...sectionProps}
              />

              {/* Done */}
              <TaskSidebarSection
                title="Done"
                tasks={doneTasks}
                totalDuration={sumDuration(doneTasks)}
                defaultCollapsed
                titleClassName="text-success"
                {...sectionProps}
              />
            </>
          ) : (
            <>
              {/* Overdue: past due date */}
              <TaskSidebarSection
                title="Overdue"
                tasks={overdueTasks}
                badgeError
                hideDuration
                titleClassName="text-destructive"
                emptyMessage="Nothing overdue — you're on track"
                {...sectionProps}
              />

              {/* Selected day: date only, no time */}
              <TaskSidebarSection
                title={selectedDayTitle}
                tasks={selectedDayTasks}
                totalDuration={sumDuration(selectedDayTasks)}
                emptyMessage="No tasks for this day"
                {...sectionProps}
              />

              {/* Scheduled on calendar: date + time */}
              <TaskSidebarSection
                title="Scheduled on calendar"
                tasks={scheduledTasks}
                totalDuration={sumDuration(scheduledTasks)}
                titleClassName="text-purple-400"
                emptyMessage={mayManageTasks ? 'Drag a task onto the calendar to schedule it' : 'Nothing scheduled'}
                {...sectionProps}
              />

              {/* To-dos: no date */}
              <TaskSidebarSection
                title="To-dos"
                tasks={unscheduledTasks}
                totalDuration={sumDuration(unscheduledTasks)}
                emptyMessage={mayManageTasks ? 'No to-dos — use + to add one' : 'No to-dos'}
                {...sectionProps}
              />

              {/* Done: completed */}
              <TaskSidebarSection
                title="Done"
                tasks={doneTasks}
                totalDuration={sumDuration(doneTasks)}
                defaultCollapsed
                titleClassName="text-success"
                emptyMessage="Completed tasks will appear here"
                {...sectionProps}
              />
            </>
          )}
      </div>
    </div>
  );
}

export default TaskSidebar;
